import React, { createContext, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";

export const ApprovalContext = createContext();

// ─── Tuning constants ─────────────────────────────────────────────────────────
// 5 minutes between fetches — reduces pressure on shared hosting
const FETCH_COOLDOWN_MS = 5 * 60 * 1000;
const API_TIMEOUT_MS    = 8000;

export const ApprovalProvider = ({ children }) => {
  const [counts, setCounts] = useState({
    rcmNewApprovalCount:       0,
    rcmInfectionApprovalCount: 0,
    rcmMigrationApprovalCount: 0,
    pdCareApprovalCount:       0,
    pendingAddressCount:       0,  // NEW: pending address approvals for CM
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const isFetching      = useRef(false);
  const lastFetchTime   = useRef(0);

  const fetchApprovalCounts = useCallback(async (force = false) => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const now   = Date.now();

    if (!token)              return;
    if (isFetching.current)  return;
    if (!force && (now - lastFetchTime.current < FETCH_COOLDOWN_MS)) return;

    isFetching.current = true;
    setLoading(true);
    setError(null);

    try {
      const decoded  = jwtDecode(token);
      const userRole = decoded.role?.toLowerCase();
      const headers  = { Authorization: `Bearer ${token}` };
      const API_URL  = process.env.REACT_APP_API_URL1;

      if (userRole === "rcm") {
        // Fetch in parallel — but catch individually so one failure doesn't break all
        const [pRes, iRes, mRes, rRes] = await Promise.all([
          axios.get(`${API_URL}/patients`,          { headers, timeout: API_TIMEOUT_MS }).catch(() => null),
          axios.get(`${API_URL}/infections`,         { headers, timeout: API_TIMEOUT_MS }).catch(() => null),
          axios.get(`${API_URL}/patient_migrations`, { headers, timeout: API_TIMEOUT_MS }).catch(() => null),
          axios.get(`${API_URL}/rcms`,               { headers, timeout: API_TIMEOUT_MS }).catch(() => null),
        ]);

        const currentRcm = rRes?.data?.rcms?.find(r => r.mobile === decoded.mobile);

        if (currentRcm?.rcm_id) {
          const rcmId = currentRcm.rcm_id;
          setCounts(prev => ({
            ...prev,
            rcmNewApprovalCount: pRes?.data?.patients?.filter(p =>
              p.rcm_id === rcmId &&
              (p.training_start_approval === "Pending" || p.drop_rcm_approval === "Pending")
            ).length || 0,
            rcmInfectionApprovalCount: iRes?.data?.infections?.filter(inf =>
              parseInt(inf.rcm, 10) === rcmId &&
              (inf.tlc_approval === "Pending" || inf.culture_approval === "Pending" || inf.outcome_approval === "Pending")
            ).length || 0,
            rcmMigrationApprovalCount: mRes?.data?.migrations?.filter(mig =>
              parseInt(mig.new_rcm, 10) === rcmId && mig.status === "Pending"
            ).length || 0,
          }));
        }

      } else if (userRole === "pdcare") {
        const pRes = await axios.get(`${API_URL}/patients`, { headers, timeout: API_TIMEOUT_MS }).catch(() => null);
        const count = pRes?.data?.patients?.filter(p =>
          (p.training_start_approval === "Approved" && p.consentform_pdapproval === "Pending") ||
          (p.training_start_approval === "Approved" && p.training_end_approval === "Pending") ||
          (p.drop_rcm_approval === "Approved" && p.drop_pdcare_approval === "Pending") ||
          p.demo_approval === "Pending"
        ).length || 0;
        setCounts(prev => ({ ...prev, pdCareApprovalCount: count }));

      } else if (userRole === "cm" || userRole === "clinicalmanager") {
        // Fetch pending address count for CM approval badge
        const aRes = await axios.get(`${API_URL}/cm/addresses/pending`, {
          headers,
          timeout: API_TIMEOUT_MS,
          params:  { per_page: 1 }   // We only need the total count, not the full list
        }).catch(() => null);

        const pendingCount = aRes?.data?.data?.total || 0;
        setCounts(prev => ({ ...prev, pendingAddressCount: pendingCount }));
      }

      lastFetchTime.current = Date.now();
    } catch (err) {
      console.error("ApprovalContext fetch error:", err);
      if (err.response?.status === 429) {
        setError("Rate limit reached. Will retry later.");
      } else {
        setError("Failed to load approval counts.");
      }
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, []);

  const value = useMemo(() => ({
    counts,
    loading,
    error,
    fetchApprovalCounts,
  }), [counts, loading, error, fetchApprovalCounts]);

  return (
    <ApprovalContext.Provider value={value}>
      {children}
    </ApprovalContext.Provider>
  );
};

